import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Ellipsis, Trash2, Pencil, X } from 'lucide-react';
import type { Conversation } from '../../store/chat.store';
import Menus from '../../components/Menus';
import { useIsMobile } from '../../hooks/useIsMobile';
import Header from '../Header';
import { type AppSurface } from '../BottomNav';

interface Props {
  conversations: Conversation[];
  activeConversationId: string | null;
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onNewConversation: () => void;
  onChangeSurface: (surface: AppSurface) => void;
}

interface PanelProps {
  onClose: () => void;
  search: string;
  setSearch: (v: string) => void;
  filtered: Conversation[];
  renamingId: string | null;
  renameValue: string;
  setRenameValue: (v: string) => void;
  renameInputRef: React.RefObject<HTMLInputElement | null>;
  startRename: (conv: Conversation) => void;
  commitRename: (id: string) => void;
  cancelRename: () => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

function useMountTransition(open: boolean, duration = 200) {
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      setEntered(false);
      const t = setTimeout(() => setMounted(false), duration);
      return () => clearTimeout(t);
    }
  }, [open, duration]);

  // Callback ref on the animated node. Reading layout (offsetWidth) forces the
  // browser to commit the closed styles before we flip to entered, so the enter
  // transition always has a starting value and never snaps open.
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && open) {
        void node.offsetWidth; // force reflow
        setEntered(true);
      }
    },
    [open],
  );

  return { mounted, entered, ref };
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function ConversationList({
  conversations,
  activeConversationId: _activeConversationId,
  open,
  onClose,
  onSelect,
  onDelete,
  onRename,
  onNewConversation,
  onChangeSurface,
}: Props) {
  const [search, setSearch] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const { mounted, entered, ref: panelRef } = useMountTransition(open);

  useEffect(() => {
    if (renamingId) renameInputRef.current?.focus();
  }, [renamingId]);

  function startRename(conv: Conversation) {
    setRenamingId(conv.id);
    setRenameValue(conv.title);
  }

  function commitRename(id: string) {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== conversations.find((c) => c.id === id)?.title) {
      onRename(id, trimmed);
    }
    setRenamingId(null);
  }

  function cancelRename() {
    setRenamingId(null);
    setRenameValue('');
  }

  const isMobile = useIsMobile();

  const filtered = conversations.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase()),
  );

  const panelProps: PanelProps = {
    onClose,
    search,
    setSearch,
    filtered,
    renamingId,
    renameValue,
    setRenameValue,
    renameInputRef,
    startRename,
    commitRename,
    cancelRename,
    onSelect,
    onDelete,
  };

  if (!mounted) return null;

  // Mobile: full-screen replacement with Header at top
  if (isMobile) {
    return (
      <div
        ref={panelRef}
        className={`fixed inset-0 z-40 mt-17.25 flex flex-col bg-white transition-transform duration-200 sm:mt-0 ${
          entered ? 'translate-x-0 ease-out' : 'translate-x-full ease-in'
        }`}
      >
        <Panel {...panelProps} />
      </div>
    );
  }

  // Wider screens: right-side drawer with dimmed backdrop
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-200 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`relative z-50 flex w-full max-w-sm flex-col bg-white shadow-xl transition-transform duration-200 ${
          entered ? 'translate-x-0 ease-out' : 'translate-x-full ease-in'
        }`}
      >
        <Panel {...panelProps} />
      </div>
    </div>
  );
}

function Panel({
  onClose,
  search,
  setSearch,
  filtered,
  renamingId,
  renameValue,
  setRenameValue,
  renameInputRef,
  startRename,
  commitRename,
  cancelRename,
  onSelect,
  onDelete,
}: PanelProps) {
  return (
    <>
      {/* Header */}
      <div className="border-ink/8 flex items-center justify-between px-4 pt-6 pb-2 sm:mb-6 sm:border-b sm:py-3 sm:pt-3">
        <div className="items-center sm:flex sm:h-11">
          <h2 className="text-ink text-lg font-semibold">History</h2>
        </div>
        <button onClick={onClose} className="hidden sm:flex">
          <X />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pb-3">
        <div className="border-ink/20 flex items-center gap-2 rounded-sm border bg-white px-3 py-2">
          <Search size={15} className="text-ink/40 shrink-0" />
          <input
            type="text"
            placeholder="Search conversations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto pt-4">
        {filtered.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-neutral-400">
            No conversations found
          </p>
        )}
        {filtered.map((conv) => (
          <div
            key={conv.id}
            className="border-ink/8 hover:bg-ink/4 flex cursor-pointer items-center justify-between overflow-hidden border-b p-4"
            onClick={() => renamingId !== conv.id && onSelect(conv.id)}
          >
            <div className="min-w-0">
              {renamingId === conv.id ? (
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename(conv.id);
                    if (e.key === 'Escape') cancelRename();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full rounded border border-neutral-300 bg-white px-2 py-0.5 text-sm text-neutral-900 outline-none focus:border-neutral-500"
                />
              ) : (
                <p className="text-ink truncate text-sm font-medium">
                  {conv.title.length > 40
                    ? conv.title.slice(0, 25) + '...'
                    : conv.title}
                </p>
              )}
              <p className="text-ink/40 text-xs">
                {formatRelativeTime(conv.updatedAt)}
              </p>
            </div>

            <Menus>
              <Menus.Toggle id={`conv-${conv.id}`}>
                <Ellipsis size={28} />
              </Menus.Toggle>
              <Menus.List id={`conv-${conv.id}`}>
                <Menus.Button
                  icon={<Pencil size={14} />}
                  onClick={() => startRename(conv)}
                >
                  Rename
                </Menus.Button>
                <Menus.Button
                  icon={<Trash2 size={14} />}
                  onClick={() => onDelete(conv.id)}
                >
                  Delete
                </Menus.Button>
              </Menus.List>
            </Menus>
          </div>
        ))}
      </div>
    </>
  );
}
